import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button, Color } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { RivalsAvatar } from '@sorare/core/src/components/rivals/RivalsAvatar';
import { FollowButton } from '@sorare/core/src/components/user/FollowButton';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { glossary } from '@sorare/core/src/lib/glossary';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MySquad } from 'components/rivals/squads/MySquad';

import { RivalsProfile_footballRivalsRoot } from './__generated__/index.graphql';
import pattern from './assets/pattern.svg';

const Root = styled(Vertical)`
  position: relative;
  isolation: isolate;
  gap: var(--double-unit);
  padding: var(--triple-unit) var(--double-unit);

  @media ${desktopAndAbove} {
    gap: var(--triple-unit);
    padding: 0 var(--triple-unit);
    border-radius: var(--double-unit);
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  &.background {
    background: center / cover url(${pattern});
  }
`;
const SpaceBetweenHorizontal = styled(Horizontal)`
  justify-content: space-between;
`;
const ButtonsWrapper = styled(Horizontal)`
  width: 100%;
  @media ${desktopAndAbove} {
    width: 70%;
    flex-direction: column;
  }
`;

type Props = {
  rivals: RivalsProfile_footballRivalsRoot;
  withBackground?: boolean;
  children: ReactNode;
};
export const RivalsProfile = ({
  rivals,
  withBackground = true,
  children,
}: Props) => {
  const { manager: rivalsManager } = rivals;
  return (
    <Root className={classNames({ background: withBackground })}>
      <RivalsAvatar size={13} manager={rivalsManager} hideStatus />
      <SpaceBetweenHorizontal>
        <MyClubLink user={rivalsManager.user}>
          <DisplayS as="h2">
            <Ellipsis>{rivalsManager.user.nickname}</Ellipsis>
          </DisplayS>
        </MyClubLink>
      </SpaceBetweenHorizontal>
      <MySquad inline rivals={rivals} />
      {children}
      <ButtonsWrapper>
        <FollowButton
          size="medium"
          subscribable={{ __typename: 'User', slug: rivalsManager.user.slug }}
          initialSubscription={rivalsManager.user.followed}
          renderButton={(buttonProps, subscription, hovered) => {
            let followButtonColor: Color = 'secondary';
            if (subscription && hovered) {
              followButtonColor = 'red';
            } else if (subscription) {
              followButtonColor = 'tertiary';
            }

            return (
              <Button
                color={followButtonColor}
                size="medium"
                fullWidth
                {...buttonProps}
              >
                {subscription ? (
                  <Horizontal>
                    <FontAwesomeIcon icon={faCheck} />
                    <FormattedMessage
                      {...(hovered ? glossary.unfollow : glossary.following)}
                    />
                  </Horizontal>
                ) : (
                  <FormattedMessage {...glossary.follow} />
                )}
              </Button>
            );
          }}
        />
      </ButtonsWrapper>
    </Root>
  );
};

RivalsProfile.fragments = {
  footballRivalsRoot: gql`
    fragment RivalsProfile_footballRivalsRoot on FootballRivalsRoot {
      id
      manager(userSlug: $user) {
        id
        user {
          id
          slug
          nickname
          followed {
            slug
          }
          ...MyClubLink_publicUserInfoInterface
        }
        ...RivalsAvatar_footballRivalsManager
      }
      ...MySquad_mySquadOverview
    }
    ${MyClubLink.fragments.user}
    ${RivalsAvatar.fragments.footballRivalsManager}
    ${MySquad.fragments.footballRivalsRoot}
  ` as TypedDocumentNode<RivalsProfile_footballRivalsRoot>,
};
